import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface Comment {
    name: string;
    email?: string;
    text: string;
    date: string;
}

interface Blog {
    _id: string;
    title: string;
    date: string;
    desc: string;
    image: string;
    views?: number;
    likes?: number;
    comments?: Comment[];
}

export default function AdminBlogs() {
    const { token } = useAuth()
    const [blogs, setBlogs] = useState<Blog[]>([])
    const [loading, setLoading] = useState(true)
    const [title, setTitle] = useState('')
    const [date, setDate] = useState(new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))
    const [desc, setDesc] = useState('')
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [showForm, setShowForm] = useState(false)
    const [viewComments, setViewComments] = useState<Comment[] | null>(null)
    const [viewBlogTitle, setViewBlogTitle] = useState('')

    useEffect(() => {
        fetchBlogs()
    }, [])

    const fetchBlogs = async () => {
        try {
            const res = await fetch(`${API_BASE}/blogs`)
            const data = await res.json()
            if (Array.isArray(data)) setBlogs(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImage(file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const resetForm = () => {
        setTitle('')
        setDesc('')
        setDate(new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))
        setImage(null)
        setPreview('')
        setEditId(null)
        setShowForm(false)
        setMsg(null)
    }

    const handleEdit = (b: Blog) => {
        setTitle(b.title)
        setDate(b.date)
        setDesc(b.desc)
        setPreview(b.image)
        setEditId(b._id)
        setMsg(null)
        setShowForm(true)
        window.scrollTo(0, 0)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!title || !desc || !token) return
        setSubmitting(true)
        setMsg(null)

        let imageUrl = preview || '/blog-1.png'

        try {
            if (image) {
                const fd = new FormData()
                fd.append('image', image)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) imageUrl = uploadData.url
            }

            const url = editId ? `${API_BASE}/blogs/${editId}` : `${API_BASE}/blogs`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ title, date, desc, image: imageUrl })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Blog updated successfully!' : 'Blog added successfully!' })
                setTimeout(() => {
                    resetForm()
                    fetchBlogs()
                }, 1500)
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to save blog' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this blog?')) return
        try {
            const res = await fetch(`${API_BASE}/blogs/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setBlogs(prev => prev.filter(b => b._id !== id))
                setMsg({ type: 'success', text: 'Blog deleted.' })
                if (editId === id) resetForm()
            }
        } catch (err) {
            console.error(err)
        }
    }

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image', 'video'],
            ['clean'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
        ],
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Article Intelligence</h1>
                    <p className="text-[14px] md:text-[15px] font-bold text-black leading-relaxed">Craft compelling narratives and manage your digital publication library.</p>
                </div>
                {showForm ? (
                    <button 
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-50 hover:bg-gray-100 text-black rounded-xl font-bold text-[13px] transition-all transform active:scale-95 group shadow-sm border border-gray-200" 
                        onClick={resetForm}
                    >
                        <i className="fa-solid fa-arrow-left-long transition-transform group-hover:-translate-x-1"></i>
                        <span>Back to Library</span>
                    </button>
                ) : (
                    <button 
                        className="flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold transition-all transform active:scale-95 group shadow-xl" 
                        onClick={() => setShowForm(true)}
                    >
                        <i className="fa-solid fa-plus transition-transform group-hover:rotate-90"></i>
                        <span>Compose Article</span>
                    </button>
                )}
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 shadow-sm border ${
                    msg.type === 'error' 
                    ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border-rose-100 dark:border-rose-800' 
                    : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800'
                }`}>
                    <i className={`fa-solid ${msg.type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-check'}`}></i>
                    <p className="text-sm font-medium">{msg.text}</p>
                </div>
            )}

            {showForm ? (
                <div className="animate-in fade-in slide-in-from-bottom-8 duration-500">
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        {/* Editor Canvas */}
                        <div className="lg:col-span-8 space-y-6">
                            <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
                                <div className="p-6 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-xl bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center text-xs shadow-lg">
                                            <i className="fa-solid fa-pen-nib"></i>
                                        </div>
                                        <h3 className="font-black text-blue-950 dark:text-white italic">Editorial Studio</h3>
                                    </div>
                                    <button 
                                        type="button"
                                        className="px-4 py-2 rounded-xl bg-gray-50 hover:bg-rose-50 text-black hover:text-rose-600 transition-all text-[12px] font-bold border border-gray-100"
                                        onClick={resetForm}
                                    >
                                        Abandon Draft
                                    </button>
                                </div>
                                <div className="p-0 admin-quill-wrapper">
                                    <ReactQuill
                                        theme="snow"
                                        value={desc}
                                        onChange={setDesc}
                                        modules={quillModules}
                                        placeholder="Start your masterpiece here..."
                                        className="h-[600px] border-none"
                                    />
                                    <div className="h-12 bg-white flex items-center px-6 border-t border-gray-100">
                                        <span className="text-[12px] font-bold text-black/60">Word Count: {desc.replace(/<[^>]*>?/gm, '').split(/\s+/).length} Words</span>
                                    </div>
                                </div>
                            </div>
                            
                            <style>{`
                                .admin-quill-wrapper .ql-toolbar.ql-snow {
                                    border: none !important;
                                    padding: 1.5rem !important;
                                    background: transparent !important;
                                    border-bottom: 1px solid #f1f5f9 !important;
                                }
                                .dark .admin-quill-wrapper .ql-toolbar.ql-snow {
                                    border-bottom: 1px solid #1e293b !important;
                                }
                                .admin-quill-wrapper .ql-container.ql-snow {
                                    border: none !important;
                                    font-size: 1rem !important;
                                    font-family: inherit !important;
                                }
                                .admin-quill-wrapper .ql-editor {
                                    padding: 2.5rem !important;
                                    min-height: 600px !important;
                                }
                                .dark .ql-snow .ql-stroke { stroke: #94a3b8 !important; }
                                .dark .ql-snow .ql-fill { fill: #94a3b8 !important; }
                                .dark .ql-snow .ql-picker { color: #94a3b8 !important; }
                            `}</style>
                        </div>

                        {/* Settings Sidebar */}
                        <div className="lg:col-span-4 lg:sticky lg:top-24 space-y-6">
                            <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none p-8 space-y-8">
                                <div>
                                    <label className="block text-[12px] font-bold text-black mb-4">Article Identity</label>
                                    <div className="space-y-4">
                                        <input 
                                            type="text" 
                                            className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-800 border-none focus:ring-2 focus:ring-black dark:text-white transition-all placeholder:text-gray-400 font-bold text-[14px] shadow-inner" 
                                            placeholder="Catchy Headline"
                                            value={title} 
                                            onChange={e => setTitle(e.target.value)} 
                                            required 
                                        />
                                        <input 
                                            type="text" 
                                            className="w-full px-5 py-4 rounded-2xl bg-white dark:bg-slate-800 border-none focus:ring-2 focus:ring-black dark:text-white transition-all placeholder:text-gray-400 font-bold text-sm shadow-inner" 
                                            placeholder="Publishing Date"
                                            value={date} 
                                            onChange={e => setDate(e.target.value)} 
                                            required 
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[12px] font-bold text-black mb-4">Featured Narrative Image</label>
                                    <div className={`relative group p-4 rounded-3xl border-2 border-dashed transition-all aspect-[4/3] flex flex-col items-center justify-center ${preview ? 'border-blue-200 dark:border-blue-900/50' : 'border-blue-900/10 dark:border-slate-800 hover:border-blue-950/50 shadow-inner'}`}>
                                        {preview ? (
                                            <div className="relative w-full h-full">
                                                <img src={getImageUrl(preview)} alt="Preview" className="w-full h-full rounded-2xl object-cover" />
                                                <button 
                                                    type="button" 
                                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-lg transform hover:scale-110 active:scale-90 transition-all"
                                                    onClick={() => { setImage(null); setPreview(''); }}
                                                >
                                                    <i className="fa-solid fa-xmark"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-image text-4xl text-black/10 transition-colors mb-3"></i>
                                                <p className="text-[11px] font-bold text-black/40 text-center">Click to upload 1200x800px</p>
                                            </>
                                        )}
                                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleImageChange} />
                                    </div>
                                </div>

                                <div className="space-y-3 pt-4">
                                    <button 
                                        type="submit" 
                                        className="w-full py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[14px] shadow-xl transition-all transform active:scale-95 flex items-center justify-center gap-3"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <div className="w-5 h-5 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-paper-plane"></i>
                                                <span>{editId ? 'Release Updates' : 'Publish Article'}</span>
                                            </>
                                        )}
                                    </button>
                                    {editId && (
                                        <button 
                                            type="button" 
                                            className="w-full py-4 text-black hover:text-black/60 transition-colors text-[13px] font-bold"
                                            onClick={resetForm}
                                        >
                                            Discard Progress
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="bg-emerald-50 dark:bg-emerald-900/10 rounded-[32px] p-6 border border-emerald-100 dark:border-emerald-800 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
                                    <i className="fa-solid fa-shield-check text-lg"></i>
                                </div>
                                <div>
                                    <p className="font-bold text-emerald-900 text-sm">Draft Protection</p>
                                    <p className="text-[11px] font-bold text-emerald-600 leading-tight">Syncing to cloud...</p>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden transition-all animate-in fade-in zoom-in-95 duration-500">
                    <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-white/50">
                        <div className="flex items-center gap-4">
                            <h3 className="text-xl font-bold text-black">Content Inventory</h3>
                            <span className="px-4 py-1 rounded-full bg-[#05144b] text-[12px] font-bold text-white">{blogs.length} Articles</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-950 rounded-xl border border-blue-900/10 dark:border-slate-800">
                                <i className="fa-solid fa-magnifying-glass text-blue-900 text-xs"></i>
                                <input type="text" className="bg-transparent border-none focus:ring-0 text-[10px] font-black text-blue-950 dark:text-white placeholder:text-blue-900 p-0 uppercase tracking-widest italic" placeholder="Search Library..." />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="p-20 text-center">
                                <div className="animate-spin w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-blue-950 dark:text-blue-950 font-medium tracking-wide">Retrieving Digital Assets...</p>
                            </div>
                        ) : (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-white border-b border-gray-100">
                                        <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black">Publication Detail</th>
                                        <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black text-center">Engagement Metrics</th>
                                        <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black text-right">Management</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {blogs.map(b => (
                                        <tr key={b._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-6">
                                                    <div className="relative flex-shrink-0 group-hover:scale-105 transition-transform duration-500">
                                                        <img src={getImageUrl(b.image)} alt={b.title} className="w-32 h-20 rounded-2xl object-cover shadow-lg group-hover:shadow-blue-500/20 transition-all" />
                                                        <div className="absolute -top-2 -left-2 w-8 h-8 rounded-xl bg-blue-600 border-4 border-white dark:border-slate-900 flex items-center justify-center text-white text-[10px]">
                                                            <i className="fa-solid fa-check"></i>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-blue-950 dark:text-white mb-2 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors italic tracking-tight text-[15px]">{b.title}</h4>
                                                        <div className="flex items-center gap-4 text-[10px] text-blue-900 font-black uppercase tracking-widest italic">
                                                            <span className="flex items-center gap-1.5"><i className="fa-regular fa-calendar-days text-blue-600"></i> {b.date}</span>
                                                            <span className="w-1 h-1 rounded-full bg-blue-100"></span>
                                                            <span className="flex items-center gap-1.5"><i className="fa-regular fa-clock text-amber-600"></i> 5 min read</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center justify-center gap-6">
                                                    <div className="text-center group-hover:scale-110 transition-transform">
                                                        <p className="text-xl font-bold text-black font-mono">{b.views || 0}</p>
                                                        <p className="text-[11px] font-bold text-black/40 mt-1">Total Impressions</p>
                                                    </div>
                                                    <div className="w-px h-8 bg-gray-100"></div>
                                                    <div className="text-center group-hover:scale-110 transition-transform">
                                                        <p className="text-xl font-bold text-black font-mono">{b.comments?.length || 0}</p>
                                                        <p className="text-[11px] font-bold text-black/40 mt-1">Discussions</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center justify-end gap-3 transition-all">
                                                    <button 
                                                        className="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                        onClick={() => { setViewComments(b.comments || []); setViewBlogTitle(b.title); }}
                                                        title="View Discussions"
                                                    >
                                                        <i className="fa-solid fa-comments text-xs"></i>
                                                    </button>
                                                    <button 
                                                        className="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                        onClick={() => handleEdit(b)}
                                                        title="Revise Narrative"
                                                    >
                                                        <i className="fa-solid fa-pen-nib text-xs"></i>
                                                    </button>
                                                    <button 
                                                        className="w-11 h-11 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                        onClick={() => handleDelete(b._id)}
                                                        title="Archive Article"
                                                    >
                                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {blogs.length === 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-8 py-20 text-center">
                                                <div className="w-20 h-20 rounded-full bg-white dark:bg-slate-800/50 flex items-center justify-center mx-auto mb-6">
                                                    <i className="fa-solid fa-scroll text-3xl text-blue-950/10"></i>
                                                </div>
                                                <h5 className="font-bold text-black mb-2">Silence in the Studio</h5>
                                                <p className="text-black/60 text-sm mb-6 font-bold">Your publication library is currently empty. Start writing your story.</p>
                                                <button 
                                                    className="px-8 py-3 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold shadow-xl transition-all"
                                                    onClick={() => setShowForm(true)}
                                                >
                                                    Craft First Masterpiece
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
            {/* Comment Viewer Modal */}
            {viewComments && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setViewComments(null)}></div>
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative z-10 animate-in zoom-in-95 slide-in-from-bottom-8 duration-500 overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                            <div>
                                <h3 className="text-xl font-bold text-black">Article Discussions</h3>
                                <p className="text-[12px] font-bold text-black/40 mt-1 line-clamp-1">{viewBlogTitle}</p>
                            </div>
                            <button 
                                className="w-10 h-10 rounded-full bg-blue-50 dark:bg-slate-800 text-blue-950 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all"
                                onClick={() => setViewComments(null)}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-8 space-y-6">
                            {viewComments.length > 0 ? (
                                viewComments.map((c, i) => (
                                    <div key={i} className="p-6 rounded-3xl bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-800 relative group">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-xl bg-[#05144b] flex items-center justify-center text-white text-[10px] font-black uppercase shadow-lg">
                                                    {c.name.charAt(0)}
                                                </div>
                                                <h4 className="font-bold text-black text-sm">{c.name}</h4>
                                            </div>
                                            <span className="text-[11px] font-bold text-black/40">{c.date}</span>
                                        </div>
                                        <p className="text-black text-xs leading-relaxed font-bold">{c.text}</p>
                                        <p className="text-[10px] font-bold text-black/40 mt-3">{c.email || 'Anonymous Identity'}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-10">
                                    <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                                        <i className="fa-regular fa-comments text-2xl text-slate-200"></i>
                                    </div>
                                    <p className="text-xs font-black text-blue-950 uppercase tracking-widest">Digital silence detected.</p>
                                </div>
                            )}
                        </div>
                        <div className="p-6 bg-white border-t border-gray-100 flex justify-end">
                            <button 
                                className="px-8 py-3 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[12px] shadow-lg transition-all active:scale-95"
                                onClick={() => setViewComments(null)}
                            >
                                Secure Consciousness
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


